import type { OrderItemsStatusSummary } from '@mntn/api-contracts/flight-deck/v1/order-items'
import { Icon, Select, SelectContent, SelectItem, SelectTrigger, Skeleton, Text } from '@mntn/ui-components'
import { useMemo } from 'react'
import { SLATE_THEME_CLASSNAME } from '@/components/shared/mfe-theme-wrapper'
import type { OrderItemsStatusFilter } from '@/flight-deck-api/services/order-items.service'

interface Props {
  status: OrderItemsStatusFilter
  setStatus: (status: OrderItemsStatusFilter) => void
  statusSummary?: OrderItemsStatusSummary
}

interface StatusOption {
  label: string
  count: number | null
  value: OrderItemsStatusFilter
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const StatusDropdown: React.FC<Props> = ({ status, setStatus, statusSummary }) => {
  const options: StatusOption[] = useMemo(() => {
    const getInFlightCount = () => {
      if (statusSummary && statusSummary.upcoming !== null && statusSummary.live !== null) {
        return (statusSummary.upcoming ?? 0) + (statusSummary.live ?? 0)
      }
      return null
    }

    const inFlightCount = getInFlightCount()

    return [
      {
        label: 'In Flight',
        count: inFlightCount,
        value: 'live',
      },
      {
        label: 'Completed',
        count: statusSummary?.completed ?? null,
        value: 'completed',
      },
    ]
  }, [statusSummary])

  const selectedOption = useMemo(() => options.find(opt => opt.value === status), [options, status])

  return (
    <div className="flex-shrink-0 text-primary-inverse items-center align-center rounded-xs">
      <Select
        // biome-ignore lint/nursery/noUnsafeTypeAssertion: RETIRED, do not copy. Fix the source type or narrow the value when touching this.
        onValueChange={val => setStatus(val as OrderItemsStatusFilter)}
        value={status}
      >
        <SelectTrigger
          className="rounded-xs bg-sub-surface-bg-inverse border-0 items-center justify-between px-6 py-4 text-header-inverse"
          iconRight={<Icon name="ChevronDown" className="text-inherit" />}
        >
          <div className="flex gap-1 items-center">
            <Text className="truncate text-header-inverse" variant="base-sb">
              {selectedOption?.label}
              {selectedOption?.count === null ? (
                <Skeleton as="span" className="inline-block h-3 w-5 align-middle ml-1" />
              ) : (
                ` (${selectedOption?.count})`
              )}
            </Text>
          </div>
        </SelectTrigger>
        {/* note: this component is rendered at the end of the document body so we need to add the slate class to the content */}
        <SelectContent
          wrapperClassName={SLATE_THEME_CLASSNAME}
          className="bg-sub-surface-bg-inverse border-0 rounded p-4 -mt-2"
        >
          {options.map(option => (
            <SelectItem
              showIcon={false}
              key={option.value ?? 'live'}
              value={option.value ?? 'live'}
              className={
                'rounded-xs text-secondary focus:text-header-inverse bg-inherit pr-0 focus:bg-surface-bg-inverse '
              }
            >
              <Text className="truncate text-inherit" variant="base-sb">
                {option.label}
                {option.count === null ? (
                  <Skeleton as="span" className="inline-block h-3 w-5 align-middle ml-1" />
                ) : (
                  ` (${option.count})`
                )}
              </Text>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
